/**
 * Mimu sticky bundle add-to-cart (launch page only).
 *
 * Mirrors the Appstle bundle widget's total + bag count in a fixed bottom bar and
 * forwards taps to the widget's REAL add-to-cart button, so the genuine bundle
 * flow runs and every discount stays intact.
 *
 * Design rule learned the hard way: the widget re-renders and can unmount its own
 * nodes while the customer is scrolled elsewhere. So nothing here may hide the bar
 * just because a DOM read came back empty. Every reading is CACHED, and the bar is
 * hidden only on a positive signal:
 *   - the widget is back on screen, or
 *   - the summary is readable AND reports zero bags, or
 *   - the customer just added to cart.
 * Anything unreadable leaves the last known state alone.
 *
 * v5: the bar no longer waits for a selection. Once the shopper is past the widget
 * it also appears EMPTY, reading "Build your bundle" and scrolling them back up to
 * the picker — the job the "Try risk-free" pill used to do. The moment the widget
 * reports one item, the bar reverts to the price/count/add-to-cart it always was.
 *
 * v6: the bar is tied to a POSITION on the page rather than merely "past the widget".
 *
 * v7: that position gate now applies to the EMPTY state ONLY. The two states have
 * different jobs, so they get different rules:
 *   - populated (>=1 in the bundle) - follows the shopper EVERYWHERE, no gate. Once
 *     they have built something, the way to buy it is always one tap away.
 *   - empty ("Build your bundle")   - only once the nutrition panel has been fully
 *     CROSSED (its bottom edge above the viewport top). Before that they are still
 *     reading the product, and being nagged to go back up would be noise.
 *
 * v8: "Build your bundle" used to scroll to the first flavour Add button minus a
 * flat 180px, which landed halfway INTO the widget. It now scrolls to the widget's
 * real top, found by climbing from the tier tabs to the first ancestor that also
 * holds the add-to-cart — that ancestor is the widget container, without having to
 * name a class Appstle does not expose. The landing point clears the sticky header,
 * so the title and the 1/2/3/4-bag tabs are all in view.
 *
 * v9: v8 still landed too low — on "Add your flavours below", i.e. BELOW the tier
 * tabs — which means the tier-tab lookup came back empty at click time and it fell
 * back to a flavour Add button. So no single anchor is trusted now. Three are
 * collected and the HIGHEST wins: the climbed widget root, the tier-tab strip, and
 * the "Build your bundle" heading found by TEXT (Appstle exposes no class for it;
 * matching visible text is the documented fallback). The heading search skips our
 * own sticky bar, whose empty label is word-for-word the same.
 *
 * v11: landing point raised 20px (EMPTY_MARGIN 12 -> 32). v10 was a mispatched
 * build that shipped byte-identical to v9 — do not reuse that filename.
 *
 * v12: the real fix. v8-v11 all subtracted --header-height, which is NOT the height
 * the header actually covers: .header-section is the sticky element and wraps the
 * countdown strip plus the logo row. So the widget kept landing partly behind the
 * header — bag labels hidden, per-EA pricing visible. Now the sticky inset is
 * MEASURED, and after the smooth scroll settles the position is re-checked and
 * corrected, so a wrong first guess can no longer survive.
 *
 * v13: adds the "$X off" pill beside the price, matching the one already shipped
 * on the cart-drawer line items (snippets/cart-products.liquid) — but computed
 * independently here, since this bar has no access to Liquid-computed cents. It
 * parses the same was/now money strings readPrices() already scrapes from
 * Appstle's rendered text and takes the difference.
 */
(function () {
  var SEL = {
    button: '.ab-mix-and-match-add-to-cart-button',
    prices: '.ab-mix-and-match-summary-prices',
    thumbs: '.ab-mix-and-match-summary-thumbnails',
    addButton: '.ab-mix-and-match-add-button',
    widget: '.appstle-bundle'
  };

  // Copy shown when the widget has nothing selected yet.
  var EMPTY_LABEL = 'Build your bundle';

  // Room left above the flavour Add button when the empty CTA scrolls back up, so
  // the product card above it lands on screen too. Matches the value the
  // "Try risk-free" pill used (BUTTON_FALLBACK_LIFT in mimu-flavor-nudge-v5.js).
  var EMPTY_LIFT = 180;

  // Breathing room left between the sticky header and the top of the widget.
  // THE dial for the landing position: raise it to stop higher up the page, lower
  // it to stop closer to the widget. 12 -> 32 in v11.
  var EMPTY_MARGIN = 32;

  // The 1/2/3/4-bag tab strip; sits above the flavour rows, below the title.
  // Proven class — snippets/mimu-tier-track.liquid draws the tier cards onto it.
  var SEL_TIER_TABS = '.ab-mix-and-match-tier-tabs';

  // Gate for the EMPTY state only: the nutrition panel must be fully behind them.
  // Anchored on the section-id SUFFIX because section ids are per-TEMPLATE, so this
  // holds for every product on the launch template. Same pattern the benefit-card
  // rules in assets/mimu-custom.css already use.
  var TRIGGER_SECTION = '[id$="__mimu_nutrition_panel_kxeNDh"]';

  var bar = document.querySelector('[data-mimu-sticky]');
  if (!bar) return;

  var wasEl = bar.querySelector('[data-mimu-sticky-was]');
  var nowEl = bar.querySelector('[data-mimu-sticky-now]');
  var offEl = bar.querySelector('[data-mimu-sticky-off]');
  var countEl = bar.querySelector('[data-mimu-sticky-count]');
  var buttonEl = bar.querySelector('[data-mimu-sticky-button]');

  // Last good readings. Never reset by an unreadable DOM.
  var state = {
    count: null,
    was: '',
    now: '',
    label: 'Add to cart',
    disabled: false,
    docTop: null, // absolute document position of the widget's add button
    docBottom: null
  };

  var scheduled = false;

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function tokens(str) {
    return (str || '').match(/[$£€¥₹]\s?\d[\d.,]*/g) || [];
  }

  /**
   * Appstle prints compare-at and payable price together ("$64.99$51.99").
   * Return both so the bar can show the saving, struck-through original first.
   * Returns null when the summary can't be read, so the caller keeps the old value.
   */
  function readPrices() {
    var box = document.querySelector(SEL.prices);
    if (!box) return null;

    // Appstle labels both prices explicitly — trust those before parsing text.
    var wasEl2 = box.querySelector('.ab-mix-and-match-summary-original-price');
    var nowEl2 = box.querySelector('.ab-mix-and-match-summary-discounted-price');
    if (nowEl2) {
      return { was: wasEl2 ? text(wasEl2) : '', now: text(nowEl2) };
    }

    var all = tokens(text(box));
    if (!all.length) return null;
    if (all.length === 1) return { was: '', now: all[0] };

    // If the markup marks the compare-at explicitly, trust that over position.
    var struck = box.querySelector('del, s, strike, [class*="compare"], [class*="strike"], [class*="original"], [class*="regular"]');
    var struckToken = struck ? tokens(text(struck))[0] : null;

    if (struckToken) {
      var payable = all.filter(function (t) { return t !== struckToken; });
      return { was: struckToken, now: payable.length ? payable[payable.length - 1] : all[all.length - 1] };
    }

    return { was: all[0], now: all[all.length - 1] };
  }

  /** Parses one money token (e.g. "$129.98") into its symbol and numeric value. */
  function parsedMoney(str) {
    var m = /([$£€¥₹])\s?([\d.,]+)/.exec(str || '');
    if (!m) return null;
    var value = parseFloat(m[2].replace(/,/g, ''));
    return isNaN(value) ? null : { symbol: m[1], value: value };
  }

  /** null = unreadable (leave cache alone); a number = authoritative. */
  function readCount() {
    // Preferred source: Appstle's per-product quantity steppers. The thumbnail
    // strip renders one image per PRODUCT, so 3x the same flavour looks like a
    // single bag — which is exactly the bug this replaces.
    var qtyEls = document.querySelectorAll('.ab-mix-and-match-quantity-value');
    if (qtyEls.length) {
      var total = 0;
      var readAny = false;
      Array.prototype.forEach.call(qtyEls, function (el) {
        var m = (el.textContent || '').match(/\d+/);
        if (m) {
          total += parseInt(m[0], 10);
          readAny = true;
        }
      });
      if (readAny) return total;
    }

    var box = document.querySelector(SEL.thumbs);
    if (!box) return null;

    // Appstle renders "No product selected" (no images) when the box is empty.
    // Never fall back to children.length here — that span counts as one bag.
    if (box.querySelector('.ab-mix-and-match-no-selection')) return 0;

    var n = box.querySelectorAll('img').length;

    // Long selections collapse into a "+N" overflow chip.
    var overflow = box.querySelector('.ab-mix-and-match-summary-overflow');
    if (overflow) {
      var extra = (overflow.textContent || '').match(/\d+/);
      if (extra) n += parseInt(extra[0], 10);
    }
    return n;
  }

  function isDisabled(el) {
    return (
      el.disabled === true ||
      el.getAttribute('aria-disabled') === 'true' ||
      el.classList.contains('ab-disabled') ||
      el.classList.contains('ab-cursor-not-allowed')
    );
  }

  /** Cache the widget's position in the DOCUMENT, not the viewport. */
  function rememberPosition(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return; // collapsed / skipped — keep old cache
    var y = window.scrollY || document.documentElement.scrollTop || 0;
    state.docTop = r.top + y;
    state.docBottom = r.bottom + y;
  }

  /**
   * Has the trigger section been fully CROSSED — scrolled past, not merely reached?
   *   true  - yes, its bottom edge is above the top of the viewport
   *   false - not yet, keep the empty CTA down
   *   null  - section not found (renamed/removed); caller should fall back rather
   *           than lock the bar off permanently.
   */
  function crossedTrigger() {
    var el = document.querySelector(TRIGGER_SECTION);
    if (!el) return null;
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return r.bottom <= 0;
  }

  /**
   * The widget's own container. Appstle exposes no root class (.appstle-bundle does
   * NOT exist), so this climbs from the tier tabs to the first ancestor that also
   * contains the add-to-cart button. The tabs sit above the flavour rows and the
   * add-to-cart below them, so their first shared ancestor IS the widget.
   * Returns null when the widget is not mounted.
   */
  function widgetRoot() {
    var tabs = document.querySelector(SEL_TIER_TABS);
    var atc = document.querySelector(SEL.button);
    var start = tabs || document.querySelector(SEL.addButton);
    if (!start) return null;
    if (!atc) return start;

    var node = start;
    while (node && node !== document.body) {
      if (node.contains(atc)) return node;
      node = node.parentElement;
    }
    return start;
  }

  /**
   * Document-top of the "Build your bundle" heading, or null.
   * Appstle gives the heading no class, so this matches on visible text — the
   * documented fallback when a class is unknown. Guards, in order: never match
   * inside our own sticky bar (its empty label is the identical phrase), only
   * heading-length text, must sit ABOVE the widget body, and must be near it
   * rather than somewhere else on the page entirely.
   */
  function titleTop(bodyTop, y) {
    var ownBar = document.querySelector('[data-mimu-sticky]');
    var nodes = document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, div');
    var best = null;

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (ownBar && ownBar.contains(el)) continue;
      if (el.children.length > 2) continue;

      var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (t.length > 60) continue;
      if (t.toLowerCase().indexOf('build your bundle') !== 0) continue;

      var r = el.getBoundingClientRect();
      if (r.height === 0) continue;

      var top = r.top + y;
      if (top > bodyTop) continue;            // must be above the widget body
      if (bodyTop - top > 1200) continue;     // ...and plausibly part of it
      if (best === null || top < best) best = top;
    }
    return best;
  }

  /**
   * Highest document-top among every anchor we can find for the widget. Returns
   * null only when the widget is not on the page at all.
   */
  function highestWidgetTop(y) {
    var tops = [];

    var root = widgetRoot();
    if (root) {
      var rr = root.getBoundingClientRect();
      if (rr.height > 0) tops.push(rr.top + y);
    }

    var tabs = document.querySelector(SEL_TIER_TABS);
    if (tabs) {
      var tr = tabs.getBoundingClientRect();
      if (tr.height > 0) tops.push(tr.top + y);
    }

    if (!tops.length) return null;

    var body = Math.min.apply(null, tops);
    var title = titleTop(body, y);
    if (title !== null) tops.push(title);

    return Math.min.apply(null, tops);
  }

  /**
   * How far down the viewport the sticky header actually reaches, measured live.
   *
   * --header-height was the old basis and it is NOT the covered height: the sticky
   * element is .header-section, which wraps the countdown strip AND the logo row,
   * while the variable tracks the inner component. Anything landed inside that
   * difference is hidden behind the header — which is why the bag labels were cut
   * off while the per-EA pricing below them stayed visible.
   */
  function stickyTopInset() {
    var els = document.querySelectorAll('.header-section, header, [id*="__header"]');
    var best = 0;

    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;

      var r = el.getBoundingClientRect();
      if (r.height === 0) continue;
      if (r.top > 4) continue;          // not parked at the top of the viewport
      if (r.bottom > best) best = r.bottom;
    }

    if (best > 0) return best;

    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return isNaN(v) ? 0 : v;
  }

  /**
   * Scroll the widget's top to just below the sticky header, then CHECK the result
   * and correct it. The check matters: the header can resize as it re-sticks and the
   * widget can reflow mid-scroll, so a single up-front calculation is a guess. This
   * measures where things actually ended up and nudges once.
   */
  function scrollWidgetIntoView() {
    var y = window.scrollY || document.documentElement.scrollTop || 0;
    var top = highestWidgetTop(y);
    if (top === null) return false;

    window.scrollTo({
      top: Math.max(0, top - stickyTopInset() - EMPTY_MARGIN),
      behavior: 'smooth'
    });

    setTimeout(function () {
      var y2 = window.scrollY || document.documentElement.scrollTop || 0;
      var top2 = highestWidgetTop(y2);
      if (top2 === null) return;

      // Where the widget top sits in the viewport vs where we want it.
      var delta = (top2 - y2) - (stickyTopInset() + EMPTY_MARGIN);
      if (Math.abs(delta) > 4) {
        window.scrollBy({ top: delta, behavior: 'smooth' });
      }
    }, 700);

    return true;
  }

  /** True only when we positively know the widget button is on screen. */
  function widgetOnScreen() {
    if (state.docTop === null) return true; // never measured — stay hidden
    var y = window.scrollY || document.documentElement.scrollTop || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return state.docBottom > y && state.docTop < y + vh;
  }

  function show(on) {
    if (bar.hidden === !on) return;
    bar.hidden = !on;
    document.documentElement.classList.toggle('mimu-sticky-on', on);
  }

  function render(empty) {
    bar.classList.toggle('mimu-sticky--empty', !!empty);

    // Nothing chosen yet: there is no count or price to mirror, so show only the CTA.
    if (empty) {
      if (buttonEl) buttonEl.textContent = EMPTY_LABEL;
      return;
    }

    if (wasEl) {
      wasEl.textContent = state.was || '';
      wasEl.hidden = !state.was;
    }
    if (nowEl) nowEl.textContent = state.now || '';

    if (offEl) {
      var wasMoney = parsedMoney(state.was);
      var nowMoney = parsedMoney(state.now);
      if (wasMoney && nowMoney && wasMoney.value > nowMoney.value) {
        offEl.textContent = wasMoney.symbol + (wasMoney.value - nowMoney.value).toFixed(2) + ' off';
        offEl.hidden = false;
      } else {
        offEl.hidden = true;
      }
    }

    if (countEl) countEl.textContent = state.count === 1 ? '1 bag' : state.count + ' bags';
    if (buttonEl && state.label) buttonEl.textContent = state.label;
  }

  function update() {
    var button = document.querySelector(SEL.button);
    if (button) {
      rememberPosition(button);
      state.disabled = isDisabled(button);
      var label = text(button);
      if (label) state.label = label;
    }

    var count = readCount();
    if (count !== null) state.count = count;

    var prices = readPrices();
    if (prices) {
      state.was = prices.was;
      state.now = prices.now;
    }

    // Positive-signal hiding only.
    // A null count means nothing has ever been read — stay hidden rather than
    // flash an empty bar before the widget has mounted.
    if (state.count === null) return show(false);

    var empty = state.count < 1;

    // The two states are gated differently — see the header comment.
    if (empty) {
      // Empty CTA is positional: hold it back until the nutrition panel is behind them.
      var crossed = crossedTrigger();
      if (crossed === null) {
        // Section missing (renamed/removed): fall back to the old "past the widget"
        // rule so it degrades gracefully instead of never showing at all.
        if (widgetOnScreen()) return show(false);
      } else if (!crossed) {
        return show(false);
      }
    } else if (state.disabled) {
      // Populated bar mirrors the widget's disabled state, but is NOT position-gated:
      // once there is something in the bundle the bar rides the whole page.
      return show(false);
    }

    render(empty);
    show(true);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      update();
    });
  }

  if (buttonEl) {
    buttonEl.addEventListener('click', function () {
      // Empty: this is the "go build one" invitation, not an add-to-cart.
      if (state.count !== null && state.count < 1) {
        if (scrollWidgetIntoView()) return;

        var y = window.scrollY || document.documentElement.scrollTop || 0;

        var anchor = document.querySelector(SEL.addButton) || document.querySelector(SEL.button);
        if (anchor) {
          var ar = anchor.getBoundingClientRect();
          window.scrollTo({ top: Math.max(0, ar.top + y - EMPTY_LIFT), behavior: 'smooth' });
        } else if (state.docTop !== null) {
          window.scrollTo({ top: Math.max(0, state.docTop - EMPTY_LIFT), behavior: 'smooth' });
        }
        return;
      }

      var button = document.querySelector(SEL.button);
      if (button && !isDisabled(button)) {
        show(false);
        button.click();
        return;
      }
      // Widget not mounted right now — bring it into view and click once it is.
      if (state.docTop !== null) {
        window.scrollTo({ top: Math.max(0, state.docTop - 120), behavior: 'smooth' });
        setTimeout(function () {
          var retry = document.querySelector(SEL.button);
          if (retry && !isDisabled(retry)) {
            show(false);
            retry.click();
          }
        }, 700);
      }
    });
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);

  var widgetObserver = null;
  function attachWidgetObserver() {
    var widget = document.querySelector(SEL.widget);
    if (!widget || widgetObserver) return;
    widgetObserver = new MutationObserver(schedule);
    widgetObserver.observe(widget, { childList: true, subtree: true, characterData: true });
  }

  new MutationObserver(function () {
    attachWidgetObserver();
    schedule();
  }).observe(document.body, { childList: true, subtree: true });

  attachWidgetObserver();
  setInterval(schedule, 800);
  schedule();
})();
